/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { ContainerRuntime, FluidDataStoreRegistry, } from "@fluidframework/container-runtime/internal";
import { 
// eslint-disable-next-line import/no-deprecated
buildRuntimeRequestHandler, } from "@fluidframework/request-handler/internal";
import { RuntimeFactoryHelper } from "@fluidframework/runtime-utils/internal";
import { DependencyContainer, } from "@fluidframework/synthesize/internal";
/**
 * BaseContainerRuntimeFactory produces container runtimes with the specified data store and service registries,
 * request handlers, runtimeOptions, and entryPoint initialization function.
 * It can be subclassed to implement a first-time initialization procedure for the containers it creates.
 * @legacy
 * @alpha
 */
export class BaseContainerRuntimeFactory extends RuntimeFactoryHelper {
    /**
     * {@inheritDoc @fluidframework/runtime-definitions#IProvideFluidDataStoreRegistry.IFluidDataStoreRegistry}
     */
    get IFluidDataStoreRegistry() {
        return this.registry;
    }
    constructor(props) {
        super();
        this.registryEntries = props.registryEntries;
        this.dependencyContainer = props.dependencyContainer;
        this.runtimeOptions = props.runtimeOptions;
        this.provideEntryPoint = props.provideEntryPoint;
        this.requestHandlers = props.requestHandlers ?? [];
        this.registry = new FluidDataStoreRegistry(this.registryEntries);
    }
    async instantiateFirstTime(runtime) {
        await this.containerInitializingFirstTime(runtime);
        await this.containerHasInitialized(runtime);
    }
    async instantiateFromExisting(runtime) {
        await this.containerHasInitialized(runtime);
    }
    async preInitialize(context, existing) {
        const scope = context.scope;
        if (this.dependencyContainer) {
            const dc = new DependencyContainer(this.dependencyContainer, scope.IFluidDependencySynthesizer);
            scope.IFluidDependencySynthesizer = dc;
        }
        return ContainerRuntime.loadRuntime({
            context,
            existing,
            runtimeOptions: this.runtimeOptions,
            registryEntries: this.registryEntries,
            containerScope: scope,
            // eslint-disable-next-line import/no-deprecated
            requestHandler: buildRuntimeRequestHandler(...this.requestHandlers),
            provideEntryPoint: this.provideEntryPoint,
        });
    }
    /**
     * Subclasses may override containerInitializingFirstTime to perform any setup steps at the time the container
     * is created. This likely includes creating any initial data stores that are expected to be there at the outset.
     * @param runtime - The container runtime for the container being initialized
     */
    async containerInitializingFirstTime(runtime) { }
    /**
     * Subclasses may override containerHasInitialized to perform any steps after the container has initialized.
     * This likely includes loading any data stores that are expected to be there at the outset.
     * @param runtime - The container runtime for the container being initialized
     */
    async containerHasInitialized(runtime) { }
}
//# sourceMappingURL=baseContainerRuntimeFactory.js.map